import * as tus from 'tus-js-client';
import { supabase } from './supabase';
import { applyResourceDefault } from './queries';
import type { EventLocationKind, PostButton, PostFormat } from './types';

// Cloudflare Stream's TUS chunk size must be a multiple of 256 KiB. 50 MiB
// (= 200 × 256 KiB) balances throughput against resumability granularity — a
// dropped connection re-sends at most one 50 MiB chunk.
const TUS_CHUNK_SIZE = 50 * 1024 * 1024;
// Stream's hard per-video size limit. Mirrors the worker guard so we fail fast
// client-side with a friendly message instead of round-tripping.
export const MAX_VIDEO_BYTES = 30 * 1024 * 1024 * 1024; // 30 GB

/**
 * Cloudflare Stream + post-type helpers. Kept out of `queries.ts` so that
 * the Stream wiring (new endpoints, media row creation, alternative post
 * creators) lives in one place and the existing query surface stays stable.
 */

// ---------- Stream upload ------------------------------------------------

export interface StreamUploadMint {
  uploadURL: string;
  uid: string;
}

/**
 * Ask the backend for a one-time TUS direct-creator upload URL + UID.
 * `fileSize` (bytes) is required — TUS creation needs the total length up
 * front. Admin/developer only (enforced server-side).
 * Throws a tagged Error on 503 (not configured) or 413 (file too large) so the
 * caller can show a friendly notice without leaking details.
 */
export async function requestStreamUploadUrl(fileSize: number): Promise<StreamUploadMint> {
  const token = await currentAccessToken();
  if (!token) throw new Error('not_authenticated');

  const res = await fetch('/api/admin/stream/upload-url', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fileSize }),
  });
  if (res.status === 503) {
    const err = new Error('stream_not_configured');
    (err as any).code = 'stream_not_configured';
    throw err;
  }
  if (res.status === 413) {
    const err = new Error('file_too_large');
    (err as any).code = 'file_too_large';
    throw err;
  }
  if (!res.ok) {
    const detail = await safeText(res);
    throw new Error(`Upload URL request failed (${res.status}): ${detail}`);
  }
  return (await res.json()) as StreamUploadMint;
}

/**
 * Upload a File to Stream's TUS direct-creator URL with progress callbacks.
 *
 * The upload URL is already created server-side (the worker did the TUS
 * creation POST), so we hand it to tus-js-client via `uploadUrl` — it HEADs
 * for the current offset and PATCHes the file in 50 MiB chunks. `retryDelays`
 * makes transient network failures resume from the last acknowledged offset
 * instead of restarting, which is the whole point for multi-GB files.
 */
export function uploadToStream(args: {
  uploadURL: string;
  file: File;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(args.file, {
      uploadUrl: args.uploadURL,
      chunkSize: TUS_CHUNK_SIZE,
      // Resume transient failures (network blips, 5xx) with backoff rather than
      // failing the whole multi-GB transfer.
      retryDelays: [0, 3000, 5000, 10000, 20000, 30000],
      onProgress: (bytesUploaded, bytesTotal) => {
        if (args.onProgress && bytesTotal > 0) {
          args.onProgress(bytesUploaded / bytesTotal);
        }
      },
      onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
      onSuccess: () => resolve(),
    });

    if (args.signal) {
      if (args.signal.aborted) {
        void upload.abort();
        reject(new Error('Stream upload aborted'));
        return;
      }
      args.signal.addEventListener('abort', () => {
        void upload.abort();
        reject(new Error('Stream upload aborted'));
      });
    }

    upload.start();
  });
}

/**
 * One-off admin maintenance: re-read each cf_stream video's true source
 * dimensions from the Stream API and fix media.aspect_ratio where it's missing
 * or wrong (so portrait clips stop letterboxing on feed cards). Idempotent.
 *
 * No UI for this — trigger it manually. Easiest: while signed in as admin in the
 * browser console, run `await (await import('/src/lib/stream-queries.ts')).…` —
 * or just POST to /api/admin/stream/backfill-aspect with your admin bearer token.
 */
export async function backfillVideoAspect(): Promise<{
  ok: true; scanned: number; updated: number; skipped: number;
  errors: Array<{ id: string; reason: string }>;
}> {
  const token = await currentAccessToken();
  if (!token) throw new Error('not_authenticated');
  const res = await fetch('/api/admin/stream/backfill-aspect', {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any)?.error ?? `Backfill failed (${res.status}).`);
  return data as any;
}

// ---------- Media row creation -------------------------------------------

export async function createVideoMedia(args: {
  uid: string;
  uploadedBy: string;
  aspectRatio?: number | null;
}): Promise<{ mediaId: string }> {
  const { data, error } = await supabase
    .from('media')
    .insert({
      kind: 'video',
      provider: 'cf_stream',
      provider_id: args.uid,
      aspect_ratio: args.aspectRatio ?? null,
      status: 'uploading',
      uploaded_by: args.uploadedBy,
    })
    .select('id')
    .single();
  if (error) throw error;
  return { mediaId: data.id };
}

export async function createImageMedia(args: {
  url: string;
  uploadedBy: string;
  aspectRatio?: number | null;
}): Promise<{ mediaId: string }> {
  const { data, error } = await supabase
    .from('media')
    .insert({
      kind: 'image',
      provider: 'supabase_storage',
      provider_id: args.url,
      aspect_ratio: args.aspectRatio ?? null,
      thumbnail_url: args.url,
      status: 'ready',
      uploaded_by: args.uploadedBy,
    })
    .select('id')
    .single();
  if (error) throw error;
  return { mediaId: data.id };
}

/**
 * Find an existing image media row by its url (provider_id), else create one.
 * Used by the edit path so re-saving a post with the same images doesn't
 * accumulate duplicate media rows on every save. Creation matches
 * createImageMedia exactly.
 */
async function findOrCreateImageMedia(args: {
  url: string;
  uploadedBy: string;
  aspectRatio?: number | null;
}): Promise<{ mediaId: string }> {
  const { data: existing, error: findErr } = await supabase
    .from('media')
    .select('id')
    .eq('kind', 'image')
    .eq('provider', 'supabase_storage')
    .eq('provider_id', args.url)
    .limit(1)
    .maybeSingle();
  if (findErr) throw findErr;
  if (existing) return { mediaId: existing.id };
  return createImageMedia(args);
}

// ---------- Playback -----------------------------------------------------

const playbackCache = new Map<string, { url: string; expiresAt: number }>();

export async function getPlaybackUrl(mediaId: string, signal?: AbortSignal): Promise<string> {
  const cached = playbackCache.get(mediaId);
  if (cached && cached.expiresAt > Date.now()) return cached.url;

  const token = await currentAccessToken();
  const res = await fetch(`/api/media/${mediaId}/stream-url`, {
    method: 'GET',
    headers: token ? { authorization: `Bearer ${token}` } : {},
    signal,
  });
  if (res.status === 503) {
    const err = new Error('stream_not_configured');
    (err as any).code = 'stream_not_configured';
    throw err;
  }
  if (!res.ok) {
    const detail = await safeText(res);
    throw new Error(`Playback URL fetch failed (${res.status}): ${detail}`);
  }
  const { playbackUrl } = (await res.json()) as { playbackUrl: string; status?: string };
  // Cache for 30 minutes client-side (signed URLs would be 1h; we're
  // conservative so a browser session picks up processing → ready transitions).
  playbackCache.set(mediaId, { url: playbackUrl, expiresAt: Date.now() + 30 * 60 * 1000 });
  return playbackUrl;
}

// ---------- Alternative post creators (image + text) ---------------------

/**
 * Insert poll_options rows for a freshly-created post. No-op when the
 * caller didn't request a poll. We require ≥2 non-empty labels — anything
 * shorter isn't a poll. Labels are trimmed and position is assigned by
 * insertion order, so the author's row order wins.
 */
async function insertPollOptions(postId: string, labels: string[] | undefined): Promise<void> {
  if (!labels) return;
  const trimmed = labels.map((l) => l.trim()).filter((l) => l.length > 0);
  if (trimmed.length < 2) return;
  const rows = trimmed.map((label, idx) => ({ post_id: postId, position: idx, label }));
  const { error } = await supabase.from('poll_options').insert(rows);
  if (error) throw error;
}

export interface NewEventInput {
  starts_at: string;
  ends_at: string | null;
  location_kind: EventLocationKind;
  location_url: string | null;
  location_address: string | null;
}

/**
 * Insert a post_events row for a freshly-created post. No-op when the
 * caller didn't request an event. The DB has a CHECK constraint mirroring
 * this validation, but we double-check client-side so we fail loudly
 * before the round-trip rather than surfacing a confusing 23514.
 */
async function insertEvent(postId: string, event: NewEventInput | undefined): Promise<void> {
  if (!event) return;
  if (event.location_kind === 'online' && !event.location_url) {
    throw new Error('Online events require a meeting URL');
  }
  if (event.location_kind === 'in_person' && !event.location_address) {
    throw new Error('In-person events require an address');
  }
  if (event.ends_at && event.ends_at <= event.starts_at) {
    throw new Error('Event end must be after start');
  }
  const { error } = await supabase.from('post_events').insert({
    post_id: postId,
    starts_at: event.starts_at,
    ends_at: event.ends_at,
    location_kind: event.location_kind,
    location_url: event.location_kind === 'online' ? event.location_url : null,
    location_address: event.location_kind === 'in_person' ? event.location_address : null,
  });
  if (error) throw error;
}

/**
 * Edit-path counterpart to insertEvent: if `event` is present, update the
 * existing post_events row (or insert it when none exists); if absent, delete
 * any existing row. Validation mirrors insertEvent.
 */
async function upsertEvent(postId: string, event: NewEventInput | undefined): Promise<void> {
  if (!event) {
    const { error } = await supabase.from('post_events').delete().eq('post_id', postId);
    if (error) throw error;
    return;
  }
  if (event.location_kind === 'online' && !event.location_url) {
    throw new Error('Online events require a meeting URL');
  }
  if (event.location_kind === 'in_person' && !event.location_address) {
    throw new Error('In-person events require an address');
  }
  if (event.ends_at && event.ends_at <= event.starts_at) {
    throw new Error('Event end must be after start');
  }
  const row = {
    post_id: postId,
    starts_at: event.starts_at,
    ends_at: event.ends_at,
    location_kind: event.location_kind,
    location_url: event.location_kind === 'online' ? event.location_url : null,
    location_address: event.location_kind === 'in_person' ? event.location_address : null,
  };
  const { error } = await supabase.from('post_events').upsert(row, { onConflict: 'post_id' });
  if (error) throw error;
}

export interface NewAuctionInput {
  lot_number: string;
  lot_name: string;
  starts_at: string;
  ends_at: string;
  min_bid: number;        // cents — opening/minimum bid the first bid must meet
  shipping_cost: number | null;  // cents; null = unspecified, 0 = free postage
  buy_now_price: number | null;  // cents; null = no Buy Now; must be >= min_bid
  shipping_info: string | null;
  size_info: string | null;
  material_info: string | null;
}

/**
 * Insert a post_auctions row for a freshly-created post. No-op when the
 * caller didn't request an auction. The bid table starts empty — the
 * first bid arrives via place_bid.
 */
async function insertAuction(postId: string, auction: NewAuctionInput | undefined): Promise<void> {
  if (!auction) return;
  if (!auction.lot_number.trim()) throw new Error('Auction lot_number is required');
  if (!auction.lot_name.trim()) throw new Error('Auction lot_name is required');
  if (auction.ends_at <= auction.starts_at) {
    throw new Error('Auction close must be after open');
  }
  if (!Number.isFinite(auction.min_bid) || auction.min_bid < 1) {
    throw new Error('Auction minimum bid is required');
  }
  if (auction.shipping_cost != null && (!Number.isFinite(auction.shipping_cost) || auction.shipping_cost < 0)) {
    throw new Error('Postage cost cannot be negative');
  }
  if (
    auction.buy_now_price != null &&
    (!Number.isInteger(auction.buy_now_price) || auction.buy_now_price < auction.min_bid)
  ) {
    throw new Error('Buy now price must be at least the starting bid');
  }
  const { error } = await supabase.from('post_auctions').insert({
    post_id: postId,
    lot_number: auction.lot_number,
    lot_name: auction.lot_name,
    starts_at: auction.starts_at,
    ends_at: auction.ends_at,
    min_bid: auction.min_bid,
    shipping_cost: auction.shipping_cost,
    buy_now_price: auction.buy_now_price,
    shipping_info: auction.shipping_info,
    size_info: auction.size_info,
    material_info: auction.material_info,
  });
  if (error) throw error;
}

/**
 * Edit-path counterpart to insertAuction: if `auction` is present, update the
 * existing post_auctions row (or insert it when none exists); if absent, delete
 * any existing row. auction_bids are never touched. Validation mirrors
 * insertAuction.
 */
async function upsertAuction(postId: string, auction: NewAuctionInput | undefined): Promise<void> {
  if (!auction) {
    const { error } = await supabase.from('post_auctions').delete().eq('post_id', postId);
    if (error) throw error;
    return;
  }
  if (!auction.lot_number.trim()) throw new Error('Auction lot_number is required');
  if (!auction.lot_name.trim()) throw new Error('Auction lot_name is required');
  if (auction.ends_at <= auction.starts_at) {
    throw new Error('Auction close must be after open');
  }
  if (!Number.isFinite(auction.min_bid) || auction.min_bid < 1) {
    throw new Error('Auction minimum bid is required');
  }
  if (auction.shipping_cost != null && (!Number.isFinite(auction.shipping_cost) || auction.shipping_cost < 0)) {
    throw new Error('Postage cost cannot be negative');
  }
  if (
    auction.buy_now_price != null &&
    (!Number.isInteger(auction.buy_now_price) || auction.buy_now_price < auction.min_bid)
  ) {
    throw new Error('Buy now price must be at least the starting bid');
  }
  const row = {
    post_id: postId,
    lot_number: auction.lot_number,
    lot_name: auction.lot_name,
    starts_at: auction.starts_at,
    ends_at: auction.ends_at,
    min_bid: auction.min_bid,
    shipping_cost: auction.shipping_cost,
    buy_now_price: auction.buy_now_price,
    shipping_info: auction.shipping_info,
    size_info: auction.size_info,
    material_info: auction.material_info,
  };
  const { error } = await supabase.from('post_auctions').upsert(row, { onConflict: 'post_id' });
  if (error) throw error;
}

export interface NewImagePostInput {
  title: string;
  slug: string;
  dek?: string | null;
  body_md?: string | null;
  drawer_ids: string[];
  image_urls: string[]; // at least one
  aspect_ratio?: number | null;
  poll_options?: string[];
  event?: NewEventInput;
  auction?: NewAuctionInput;
}

export async function adminCreateImagePost(authorId: string, input: NewImagePostInput): Promise<string> {
  if (input.image_urls.length === 0) throw new Error('At least one image is required');
  const first = input.image_urls[0];

  const { mediaId } = await createImageMedia({
    url: first,
    uploadedBy: authorId,
    aspectRatio: input.aspect_ratio ?? null,
  });

  const { data: post, error: pErr } = await supabase
    .from('posts')
    .insert({
      slug: input.slug,
      title: input.title,
      dek: input.dek ?? null,
      body_md: input.body_md ?? null,
      post_type: 'image',
      author_id: authorId,
      primary_media_id: mediaId,
      image_urls: input.image_urls,
      published_at: new Date().toISOString(),
    })
    .select('id, slug')
    .single();
  if (pErr) throw pErr;

  if (input.drawer_ids.length) {
    const rows = input.drawer_ids.map((d) => ({ post_id: post.id, drawer_id: d }));
    const { error: jErr } = await supabase.from('post_drawers').insert(rows);
    if (jErr) throw jErr;
  }
  await insertPollOptions(post.id, input.poll_options);
  await insertEvent(post.id, input.event);
  await insertAuction(post.id, input.auction);
  return post.slug;
}

export interface NewTextPostInput {
  title: string;
  slug: string;
  dek: string;
  body_md: string;
  drawer_ids: string[];
  poll_options?: string[];
  event?: NewEventInput;
  auction?: NewAuctionInput;
}

export async function adminCreateTextPost(authorId: string, input: NewTextPostInput): Promise<string> {
  if (!input.dek?.trim()) throw new Error('Dek is required for text posts');
  if (!input.body_md?.trim()) throw new Error('Body is required for text posts');

  const { data: post, error: pErr } = await supabase
    .from('posts')
    .insert({
      slug: input.slug,
      title: input.title,
      dek: input.dek,
      body_md: input.body_md,
      post_type: 'text',
      author_id: authorId,
      primary_media_id: null,
      published_at: new Date().toISOString(),
    })
    .select('id, slug')
    .single();
  if (pErr) throw pErr;

  if (input.drawer_ids.length) {
    const rows = input.drawer_ids.map((d) => ({ post_id: post.id, drawer_id: d }));
    const { error: jErr } = await supabase.from('post_drawers').insert(rows);
    if (jErr) throw jErr;
  }
  await insertPollOptions(post.id, input.poll_options);
  await insertEvent(post.id, input.event);
  await insertAuction(post.id, input.auction);
  return post.slug;
}

export interface NewVideoPostInput {
  title: string;
  slug: string;
  dek?: string | null;
  body_md?: string | null;
  drawer_ids: string[];
  mediaId: string; // created via createVideoMedia
  cover_url?: string | null;
  aspect_ratio?: number | null;
  poll_options?: string[];
  event?: NewEventInput;
  auction?: NewAuctionInput;
}

export async function adminCreateVideoPost(authorId: string, input: NewVideoPostInput): Promise<string> {
  // If a cover was picked, stash it on the media row as thumbnail_url so the
  // feed card and share image have something before Stream finishes processing.
  if (input.cover_url) {
    const { error: mErr } = await supabase
      .from('media')
      .update({ thumbnail_url: input.cover_url })
      .eq('id', input.mediaId);
    if (mErr) console.warn('[adminCreateVideoPost] cover update failed', mErr);
  }

  const { data: post, error: pErr } = await supabase
    .from('posts')
    .insert({
      slug: input.slug,
      title: input.title,
      dek: input.dek ?? null,
      body_md: input.body_md ?? null,
      post_type: 'video',
      author_id: authorId,
      primary_media_id: input.mediaId,
      published_at: new Date().toISOString(),
    })
    .select('id, slug')
    .single();
  if (pErr) throw pErr;

  if (input.drawer_ids.length) {
    const rows = input.drawer_ids.map((d) => ({ post_id: post.id, drawer_id: d }));
    const { error: jErr } = await supabase.from('post_drawers').insert(rows);
    if (jErr) throw jErr;
  }
  await insertPollOptions(post.id, input.poll_options);
  await insertEvent(post.id, input.event);
  await insertAuction(post.id, input.auction);
  return post.slug;
}

// ---------- Unified wizard post creator ----------------------------------
// The new-model creator the post wizard drives. Supports the standard/redesign
// format split, an ORDERED list of mixed media items (already-uploaded video
// mediaIds + newly-uploaded image urls), team-defined buttons, the per-post
// resource override, drawers, optional poll/event/auction, and draft-vs-
// publish. It keeps image_urls[] + primary_media_id populated for back-compat
// (closet-door / feed / archive covers read those), AND writes the post_media
// rows that the multi-media viewer reads as its source of truth.

/** One ordered item in the wizard's media list. A video reuses a mediaId
 *  already created via createVideoMedia; an image carries its uploaded URL
 *  (we synthesise the media row, same as createImageMedia). A video's
 *  thumbnailUrl is its custom player poster (null = first frame). */
export type WizardMediaItem =
  | { kind: 'video'; mediaId: string; thumbnailUrl?: string | null }
  | { kind: 'image'; url: string; aspectRatio?: number | null };

export interface NewWizardPostInput {
  title: string;
  slug: string;
  dek?: string | null;
  body_md?: string | null;
  format?: PostFormat;
  drawer_ids: string[];
  /** Ordered media items (mixed image + video). Empty = text post. */
  media: WizardMediaItem[];
  buttons?: PostButton[];
  /** Per-post trace-resource override (redesign posts). When it differs from
   *  the drawer default, the drawer default is updated going forward. */
  resource_url?: string | null;
  /** Optional per-post feed/closet cover. Null = fall back to first media. */
  thumbnail_url?: string | null;
  /** Hide the text title on the post page + feed card. Default false. */
  hide_title?: boolean;
  poll_options?: string[];
  event?: NewEventInput;
  auction?: NewAuctionInput;
  /** false (default) publishes now; true leaves published_at null (draft). */
  draft?: boolean;
  /** When publishing (draft = false), the time to go live. A future time
   *  schedules the post (hidden until then via RLS). Defaults to now. Ignored
   *  when draft = true. */
  publish_at?: string | null;
}

export async function adminCreateWizardPost(
  authorId: string,
  input: NewWizardPostInput,
): Promise<{ id: string; slug: string }> {
  const media = input.media ?? [];
  const first = media[0];
  // post_type derives from the first media item (matches the cover the
  // closet-door/feed/archive thumbnails read), text when there's no media.
  const postType: 'video' | 'image' | 'text' =
    first?.kind === 'video' ? 'video' : first?.kind === 'image' ? 'image' : 'text';

  // Resolve each media item to a media row id, preserving order. Videos reuse
  // the supplied mediaId; images synthesise a media row (createImageMedia).
  // Collect image urls for the back-compat image_urls[].
  const { mediaIds, imageUrls } = await resolveWizardMedia(media, authorId, createImageMedia);
  const primaryMediaId = mediaIds[0] ?? null;
  await applyVideoThumbnails(media);

  const { data: post, error: pErr } = await supabase
    .from('posts')
    .insert({
      slug: input.slug,
      title: input.title,
      dek: input.dek ?? null,
      body_md: input.body_md ?? null,
      post_type: postType,
      format: input.format ?? 'standard',
      author_id: authorId,
      primary_media_id: primaryMediaId,
      image_urls: imageUrls,
      buttons: input.buttons ?? [],
      resource_url: input.resource_url ?? null,
      thumbnail_url: input.thumbnail_url ?? null,
      hide_title: input.hide_title ?? false,
      published_at: input.draft ? null : (input.publish_at ?? new Date().toISOString()),
    })
    .select('id, slug')
    .single();
  if (pErr) throw pErr;

  // Ordered post_media rows — source of truth for the multi-media viewer.
  if (mediaIds.length > 0) {
    const rows = mediaIds.map((mediaId, position) => ({
      post_id: post.id,
      media_id: mediaId,
      position,
    }));
    const { error: pmErr } = await supabase.from('post_media').insert(rows);
    if (pmErr) throw pmErr;
  }

  if (input.drawer_ids.length) {
    const rows = input.drawer_ids.map((d) => ({ post_id: post.id, drawer_id: d }));
    const { error: jErr } = await supabase.from('post_drawers').insert(rows);
    if (jErr) throw jErr;
  }

  await insertPollOptions(post.id, input.poll_options);
  await insertEvent(post.id, input.event);
  await insertAuction(post.id, input.auction);

  // Redesign "save as default from then on": persist the override as the
  // drawer default when it differs.
  if ((input.format ?? 'standard') === 'redesign') {
    await applyResourceDefault(input.drawer_ids, input.resource_url);
  }

  return { id: post.id, slug: post.slug };
}

/**
 * Resolve the ordered wizard media list to (ordered media row ids, image urls).
 * Videos reuse their supplied mediaId; images are resolved to a media row via
 * `imageResolver` — create-always for the create path, find-or-create for the
 * edit path (so re-saving doesn't duplicate media rows).
 */
async function resolveWizardMedia(
  media: WizardMediaItem[],
  authorId: string,
  imageResolver: (args: { url: string; uploadedBy: string; aspectRatio?: number | null }) => Promise<{ mediaId: string }>,
): Promise<{ mediaIds: string[]; imageUrls: string[] }> {
  const mediaIds: string[] = [];
  const imageUrls: string[] = [];
  for (const item of media) {
    if (item.kind === 'video') {
      mediaIds.push(item.mediaId);
    } else {
      const { mediaId } = await imageResolver({
        url: item.url,
        uploadedBy: authorId,
        aspectRatio: item.aspectRatio ?? null,
      });
      mediaIds.push(mediaId);
      imageUrls.push(item.url);
    }
  }
  return { mediaIds, imageUrls };
}

/**
 * Write each video's custom thumbnail onto its media row. The player, the
 * carousel, the lightbox and the feed cards all read media.thumbnail_url as
 * that video's poster; the cf-stream webhook never touches the column, so a
 * custom value survives encoding. Null clears it (back to the first frame).
 * Best-effort: a failed write shouldn't sink the whole publish.
 */
async function applyVideoThumbnails(media: WizardMediaItem[]): Promise<void> {
  for (const item of media) {
    if (item.kind !== 'video') continue;
    const { error } = await supabase
      .from('media')
      .update({ thumbnail_url: item.thumbnailUrl ?? null })
      .eq('id', item.mediaId);
    if (error) console.warn('[wizard] video thumbnail update failed', error);
  }
}

/**
 * Update an existing post from the wizard. Same input shape as create.
 * published_at is preserved so a draft stays a draft and a live post stays
 * live (publish/schedule transitions go through setPostSchedule). The slug is
 * written through (edit mode re-sends the same locked value; autosaved drafts
 * keep it in sync). Replaces the post's media,
 * drawers, poll, event and auction to mirror the wizard state exactly. Returns
 * the (unchanged) slug.
 */
export async function adminUpdateWizardPost(
  postId: string,
  input: NewWizardPostInput,
): Promise<string> {
  const media = input.media ?? [];
  const first = media[0];
  const postType: 'video' | 'image' | 'text' =
    first?.kind === 'video' ? 'video' : first?.kind === 'image' ? 'image' : 'text';

  // Author of record for any newly-synthesised image media rows.
  const { data: postRow, error: rErr } = await supabase
    .from('posts')
    .select('author_id, slug')
    .eq('id', postId)
    .single();
  if (rErr) throw rErr;
  const authorId = postRow.author_id as string;

  const { mediaIds, imageUrls } = await resolveWizardMedia(
    media,
    authorId,
    findOrCreateImageMedia,
  );
  const primaryMediaId = mediaIds[0] ?? null;
  await applyVideoThumbnails(media);

  // Update the posts row. slug + published_at are intentionally untouched.
  const { error: uErr } = await supabase
    .from('posts')
    .update({
      // slug is safe to update: edit mode re-sends the same (locked) slug, while
      // an autosaved draft needs its slug kept in sync with the wizard field
      // until it's published. published_at is intentionally never touched here.
      slug: input.slug,
      title: input.title,
      dek: null,
      body_md: input.body_md ?? null,
      post_type: postType,
      format: input.format ?? 'standard',
      primary_media_id: primaryMediaId,
      image_urls: imageUrls,
      buttons: input.buttons ?? [],
      resource_url: input.resource_url ?? null,
      thumbnail_url: input.thumbnail_url ?? null,
      hide_title: input.hide_title ?? false,
      updated_at: new Date().toISOString(),
    })
    .eq('id', postId);
  if (uErr) throw uErr;

  // Replace post_media with the new ordered set.
  {
    const { error: delErr } = await supabase.from('post_media').delete().eq('post_id', postId);
    if (delErr) throw delErr;
    if (mediaIds.length > 0) {
      const rows = mediaIds.map((mediaId, position) => ({ post_id: postId, media_id: mediaId, position }));
      const { error: insErr } = await supabase.from('post_media').insert(rows);
      if (insErr) throw insErr;
    }
  }

  // Replace post_drawers.
  {
    const { error: delErr } = await supabase.from('post_drawers').delete().eq('post_id', postId);
    if (delErr) throw delErr;
    if (input.drawer_ids.length) {
      const rows = input.drawer_ids.map((d) => ({ post_id: postId, drawer_id: d }));
      const { error: insErr } = await supabase.from('post_drawers').insert(rows);
      if (insErr) throw insErr;
    }
  }

  // Replace poll. Clearing poll_options cascades poll_votes — acceptable.
  {
    const { error: delErr } = await supabase.from('poll_options').delete().eq('post_id', postId);
    if (delErr) throw delErr;
    await insertPollOptions(postId, input.poll_options);
  }

  // Upsert / clear the event.
  await upsertEvent(postId, input.event);
  // Upsert / clear the auction (auction_bids are left untouched).
  await upsertAuction(postId, input.auction);

  if ((input.format ?? 'standard') === 'redesign') {
    await applyResourceDefault(input.drawer_ids, input.resource_url);
  }

  return postRow.slug as string;
}

// ---------- Internals ----------------------------------------------------

async function currentAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function safeText(res: Response): Promise<string> {
  try { return await res.text(); } catch { return ''; }
}
